import { getMenuAuth } from "src/apis/authApi";
import { useCallback, useEffect } from "react";
import { getBomTree, exportBomExcel } from "./Api_MST1030_R01";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import useMST1030_R01Store, { MST1030_R01Filter, TabType } from "./Store_MST1030_R01";
import { MST1030_R01Dto } from "./Dto_MST1030_R01";
import { flattenTreeForDisplay } from "./Utils_MST1030_R01";

interface BomContentProps {
    setMessage: (message: string) => void;
}

export const Content_MST1030_R01 = (props: BomContentProps) => {
    const { setMessage } = props;

    const {
        MST1030_R01Filter,
        activeTab, setActiveTab,
        setTabData,
        setTabScrollTop,
    } = useMST1030_R01Store();

    const { setLoadingSpinner } = useLoadingSpinnerStore();
    const setAuth = useActionbarAuthStore((state) => state.setAuth);

    useFetchActionbarAuth("MST1030_R01");

    useEffect(() => {
        getMenuAuth("MST1030_R01").then((res) => {
            setAuth(res.data);
        });
    }, []);

    const getParams = (filter: Partial<MST1030_R01Filter>) => ({
        searchText: filter.searchText,
        itemGroup: filter.itemGroup,
        itemAccount: filter.itemAccount,
    });

    const fetchTabData = useCallback(async (tab: TabType) => {
        setLoadingSpinner(true);
        setTabScrollTop(tab, null);
        try {
            const response = await getBomTree(getParams(MST1030_R01Filter), tab);

            if (response.data.successFlag) {
                const treeData = response.data.data as MST1030_R01Dto[];
                const flattenedData = flattenTreeForDisplay(treeData, tab);

                setTabData(tab, flattenedData);

                if (flattenedData.length > 0) {
                    const label = tab === 'forward' ? '정전개' : '역전개';
                    ShowSuccessFetch(setMessage,
                        `BOM ${label} 조회가 완료되었습니다. (루트: ${treeData.length}개, 총: ${flattenedData.length}건)`);
                    setTimeout(() => setMessage(""), 5000);
                } else {
                    showWarning('데이터가 없습니다.');
                }
            } else {
                showWarning(response.data.message || 'BOM 조회에 실패했습니다.');
                setTabData(tab, []);
            }
        } catch (error) {
            console.error("BOM 조회 중 오류:", error);
            showWarning("BOM 조회 중 오류가 발생했습니다.");
        } finally {
            setLoadingSpinner(false);
        }
    }, [MST1030_R01Filter, setTabData, setTabScrollTop, setLoadingSpinner, setMessage]);

    const fetchData = useCallback(async () => {
        await fetchTabData(activeTab);
    }, [activeTab, fetchTabData]);

    const handleTabChange = useCallback(async (tab: TabType) => {
        if (tab === activeTab) return;
        setActiveTab(tab);
        setTabData(tab, []);
        await fetchTabData(tab);
    }, [activeTab, setActiveTab, setTabData, fetchTabData]);

    const handleExcelClick = useCallback(async () => {
        const response = await exportBomExcel(getParams(MST1030_R01Filter), activeTab);
        const label = activeTab === 'forward' ? 'BOM 정전개' : 'BOM 역전개';
        downloadSettingExcel({
            responseData: response.data.data,
            fileName: `${label}-excel.xlsx`,
        });
    }, [MST1030_R01Filter, activeTab]);

    return {
        fetchData,
        handleExcelClick,
        handleTabChange,
    };
};
